import { logger } from '../../../modules/utils/apilogger';
import { numberWithCommas } from '../../../modules/utils/numericalFormatsHelper';

/**
 * Currencies that show $ symbol in en-US locale
 * Per content owners: Only USD and CAD gift cards are supported
 * and only these show the $ symbol in en-US
 */
const DOLLAR_CURRENCIES = ['USD', 'CAD'];

/**
 * Determines if currency symbol should be shown
 * Per content owners: Only en-US locale shows $ symbol for USD/CAD
 * All other locales show currency code only (e.g., "50,00 USD")
 */
const shouldShowSymbol = (cardCurrency: string, locale: string): boolean => {
  // Only show $ symbol for USD/CAD in en-US locale
  return locale.toLowerCase() === 'en-us' && DOLLAR_CURRENCIES.includes(cardCurrency);
};

/**
 * Determines if currency code should be shown
 * Always show currency code for all locales
 */
const shouldShowCurrencyCode = (_cardCurrency: string, _locale: string): boolean => {
  return true;
};

/**
 * Extracts currency symbol using Intl.NumberFormat
 * Only returns $ symbol for USD/CAD in en-US locale per content owner requirements
 * @param cardCurrency - The currency code (e.g., "USD", "CAD")
 * @param locale - Locale for formatting
 * @returns Currency symbol ("$") or empty string
 */
const extractCurrencySymbol = (cardCurrency: string, locale: string): string => {
  try {
    // Only show symbol for USD/CAD in en-US locale
    if (!shouldShowSymbol(cardCurrency, locale)) {
      return '';
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cardCurrency,
      currencyDisplay: 'narrowSymbol',
    });
    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find(part => part.type === 'currency');
    return currencyPart?.value || '';
  } catch {
    return '';
  }
};

/**
 * Formats currency amount using proper locale and currency formatting
 * @param certificateBalance - The numeric balance amount
 * @param cardCurrency - The currency code (e.g., "USD", "EUR")
 * @param locale - Optional locale for formatting (defaults to 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (certificateBalance: number, cardCurrency: string, locale: string = 'en-US'): string => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cardCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(certificateBalance);
  } catch (error) {
    const { log } = logger({})('formatCurrency');

    log.warn(`Currency formatting failed for ${cardCurrency}`, error);

    return `${numberWithCommas(certificateBalance.toFixed(2))} ${cardCurrency}`;
  }
};

/**
 * Extracts currency symbol, value, and currency code for custom styling
 * Uses Intl.NumberFormat.formatToParts() to properly extract locale-aware components
 * @param certificateBalance - The numeric balance amount
 * @param cardCurrency - The currency code (e.g., "USD", "CAD")
 * @param locale - Optional locale for formatting (defaults to 'en-US')
 * @returns Object with symbol, value, and currencyCode for separate styling
 */
export const parseCurrencyComponents = (
  certificateBalance: number,
  cardCurrency: string,
  locale: string = 'en-US'
): {
  symbol: string;
  value: string;
  currencyCode: string;
} => {
  try {
    const symbol = extractCurrencySymbol(cardCurrency, locale);

    const numberFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const numericValue = numberFormatter.format(certificateBalance);

    // Determine if currency code should be shown
    const showCurrencyCode = shouldShowCurrencyCode(cardCurrency, locale);

    return {
      symbol,
      value: numericValue,
      currencyCode: showCurrencyCode ? cardCurrency : '',
    };
  } catch (error) {
    const { log } = logger({})('parseCurrencyComponents');
    log.warn(`Currency parsing failed for ${cardCurrency}:`, error);
    return {
      symbol: '',
      value: numberWithCommas(certificateBalance?.toFixed(2)),
      currencyCode: cardCurrency,
    };
  }
};

/**
 * Comprehensive currency formatter that handles both scenarios and returns styled components:
 * - When you have both cardCurrency and cardCurrencyCode
 * - When you only have cardCurrencyCode and need to derive cardCurrency
 * Returns separate components for custom styling: symbol (small), value (large), currencyCode (small)
 */
export const formatGiftCardCurrency = (
  certificateBalance: number,
  cardCurrency?: string,
  locale: string = 'en-US'
): {
  symbol: string;
  value: string;
  currencyCode: string;
  fullFormatted: string; // Keep this for backward compatibility
} => {
  const resolvedCurrency = cardCurrency;

  if (resolvedCurrency) {
    const components = parseCurrencyComponents(certificateBalance, resolvedCurrency, locale);
    return {
      ...components,
      fullFormatted: formatCurrency(certificateBalance, resolvedCurrency, locale),
    };
  }

  // Fallback if neither is available
  return {
    symbol: '',
    value: numberWithCommas(certificateBalance?.toFixed(2)),
    currencyCode: '',
    fullFormatted: numberWithCommas(certificateBalance?.toFixed(2)),
  };
};
